import type { Maybe } from '@@types/helpers'
import { useAppRouter } from '@app/hooks'
import type { AppRouteName } from '@app/routes'
import type { RouterUpdate } from '@libs/Router/AppRouter'
import type { IRouteInfo } from '@libs/Router/types'
import React from 'react'
import type { DataRouter } from 'react-router-dom'

export interface IUseHistoryChangeOptions {
  onHistoryChange?: (
    currentRouteInfo: Maybe<IRouteInfo<AppRouteName, any>>,
    update: RouterUpdate
  ) => void
}

export interface IUseHistoryChangeOutput {
  history: Maybe<DataRouter>
  currentRouteInfo: Maybe<IRouteInfo<AppRouteName, any>>
}

/**
 * Retrieve router and bind to router state changes to save the currentRouteName.
 */
export function useHistoryChange(
  options?: IUseHistoryChangeOptions
): IUseHistoryChangeOutput {
  const [routerState, setRouter] = React.useState<Maybe<DataRouter>>(null)

  const [currentRouteInfoState, setCurrentRouteInfo] =
    React.useState<Maybe<IRouteInfo<AppRouteName, any>>>(null)

  const appRouter = useAppRouter()

  // First, save the router object into the state
  React.useEffect(() => {
    if (routerState) {
      return
    }

    const routerObject = appRouter.routerObject()

    if (routerObject) {
      setRouter(routerObject)
    }
  }, [])

  // Second, set the currentRouteName and subscribe to router changes to update
  // the currentRouteName
  React.useEffect(() => {
    if (!routerState) {
      return
    }

    setCurrentRouteInfo(appRouter.getCurrentRouteInfo())

    const unsubscribe = routerState.subscribe(state => {
      const routeInfo = appRouter.getCurrentRouteInfo()
      const update: RouterUpdate = {
        location: state.location,
        action: state.historyAction
      }

      setCurrentRouteInfo(routeInfo)

      if (options?.onHistoryChange) {
        options.onHistoryChange(routeInfo, update)
      }
    })

    return () => {
      if (unsubscribe) {
        unsubscribe()
      }
    }
  }, [routerState, setCurrentRouteInfo])

  return { history: routerState, currentRouteInfo: currentRouteInfoState }
}
